import { Tabs, Text } from "@mantine/core";
import classes from "./styles.module.css";
import AddressTable from "../../components/management/AddressTable";
import UsersTable from "../../components/management/UsersTable";
import OrganisationTable from "../../components/management/OrganisationTable";
import UserRoleFunctionManagement from "../../components/management/UserRoleFunctionManagement";
import ContractsTable from "../../components/management/ContractsTable";
import ContractUsersManagement from "../../components/management/ContractUsersManagement";
import ContractDefaultUsersManagement from "../../components/management/ContractDefaultUsersManagement";
import ExportConfigManagement from "../../components/management/ExportConfigManagement";
import EmailNotificationSettings from "../../components/management/EmailNotificationSettings";
import StatusColourManagement from "../../components/management/StatusColourManagement";
import { useEffect, useState } from "react";
import useTransientStore from "../../data/transcientStore";
import {
  getAllAddressSystems,
  getAllOrganisations,
  getAllRoles,
  getAllUsers,
} from "../../services/UserService";
import { useTranslation } from "../../hooks/useTranslation";

interface AdminPanelProps {
  dashboardType: "users" | "contracts";
  activeAdminTab: string;
  onAdminTabChange: (value: string | null) => void;
}

export default function AdminPanel({
  dashboardType,
  activeAdminTab,
  onAdminTabChange,
}: AdminPanelProps) {
  const selectedAddressSystem = useTransientStore(
    (state) => state.selectedAddressSystem,
  );
  const addrIdFromStore = selectedAddressSystem?.id || 0;

  const token = useTransientStore((state) => state.token);
  const userData = useTransientStore((state) => state.allUsers);
  const setUserData = useTransientStore((state) => state.setAllUsers);
  const organisationData = useTransientStore((state) => state.allOrganisations);
  const setAllOrganisations = useTransientStore(
    (state) => state.setAllOrganisations,
  );
  const addressSystemData = useTransientStore(
    (state) => state.allAddressSystems,
  );
  const setAllAddressSystems = useTransientStore(
    (state) => state.setAllAddressSystems,
  );
  const roleData = useTransientStore((state) => state.allRoles);
  const setRoleData = useTransientStore((state) => state.setAllRoles);

  const fetchUsers = async () => {
    const users = await getAllUsers();
    setUserData(users);
  };

  const fetchOrganisations = async () => {
    const organisations = await getAllOrganisations();
    setAllOrganisations(organisations);
  };

  const fetchAddressSystems = async () => {
    const addressSystems = await getAllAddressSystems();
    setAllAddressSystems(addressSystems);
  };

  const fetchRoles = async () => {
    const roles = await getAllRoles();
    setRoleData(roles);
  };

  useEffect(() => {
    fetchUsers();
    fetchOrganisations();
    fetchAddressSystems();
    fetchRoles();
  }, [token]);

  return (
    <div className={classes.adminContent}>
      {dashboardType === "users" && (
        <>
          {activeAdminTab === "users" && (
            <UsersTable
              userData={userData}
              roles={roleData}
              refreshUsers={fetchUsers}
              refreshAddressSystems={fetchAddressSystems}
            />
          )}
          {activeAdminTab === "organisations" && (
            <OrganisationTable
              organisationData={organisationData}
              refreshOrganisations={fetchOrganisations}
            />
          )}
          {activeAdminTab === "address_systems" && (
            <AddressTable
              addressSystemData={addressSystemData}
              refreshAddressSystems={fetchAddressSystems}
              refreshUsers={fetchUsers}
            />
          )}
          {activeAdminTab === "role_management" && (
            <UserRoleFunctionManagement />
          )}
          {activeAdminTab === "export_config" && <ExportConfigManagement />}
        </>
      )}

      {dashboardType === "contracts" && (
        <>
          {activeAdminTab === "contracts" && <ContractsTable />}
          {activeAdminTab === "contract_users" && <ContractUsersManagement />}
          {activeAdminTab === "contract_default_users" && (
            <ContractDefaultUsersManagement />
          )}
          {activeAdminTab === "email_notification_settings" && (
            <EmailNotificationSettings />
          )}
          {activeAdminTab === "status_colours" && <StatusColourManagement />}
        </>
      )}
    </div>
  );
}
